<template lang="">
  <section class="jobs-hero">
    <div class="jobs-hero__inner">
      <Eyebrow>Vagas Abertas</Eyebrow>
      <h1 class="jobs-hero__title">Indique e ganhe por cada<br />profissional contratado</h1>
      <p class="jobs-hero__sub">
        Navegue pelas vagas abertas, encontre profissionais da sua rede e faça sua indicação.
      </p>

      <div class="jobs-hero__search">
        <div class="jobs-hero__search-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="jobs-hero__search-icon">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="jobs-hero__search-input"
            placeholder="Cargo, empresa, tecnologia..."
          />
          <span class="jobs-hero__search-btn">Buscar</span>
        </div>
        <span class="jobs-hero__search-note">
          A busca considera apenas as vagas já carregadas nesta página.
        </span>
      </div>

      <div class="jobs-hero__stats">
        <div class="jobs-hero__stat">
          <span class="jobs-hero__stat-val">{{ heroStatValue }}</span>
          <span class="jobs-hero__stat-label">vagas disponíveis</span>
        </div>
      </div>
    </div>
  </section>

  <div class="jobs-main">
    <aside class="jobs-sidebar">
      <div class="jobs-sidebar__title-row">
        <span class="jobs-sidebar__title">Filtros</span>
        <button type="button" class="jobs-sidebar__clear" @click="resetFilters">Limpar</button>
      </div>

      <div v-if="activeFilters.length" class="jobs-sidebar__tags">
        <button
          v-for="entry in activeFilters"
          :key="entry.groupKey"
          type="button"
          class="jobs-sidebar__tag"
          @click="removeFilter(entry)"
        >
          {{ entry.label }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div v-for="group in state.options" :key="group.key" class="jobs-sidebar__group">
        <button
          type="button"
          class="jobs-sidebar__group-header"
          :aria-expanded="state.openGroupKey === group.key"
          @click="toggleGroup(group.key)"
        >
          <span class="jobs-sidebar__group-title">{{ group.title }}</span>
          <svg
            class="jobs-sidebar__group-chevron"
            :class="{ 'jobs-sidebar__group-chevron--open': state.openGroupKey === group.key }"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div
          class="jobs-sidebar__group-body"
          :class="{ 'jobs-sidebar__group-body--open': state.openGroupKey === group.key }"
        >
          <div class="jobs-sidebar__chips">
            <FilterChip
              v-for="item in group.data.slice(1)"
              :key="item.value"
              :model-value="item.default"
              @update:modelValue="selectFilter(group, item)"
            >
              {{ item.description }}
            </FilterChip>
          </div>
        </div>
      </div>
    </aside>

    <div class="jobs-list">
      <div class="jobs-list__meta">
        <span class="jobs-list__count">{{ countLabel }}</span>
      </div>

      <div v-if="!cache && jobsLoading" class="jobs-list__loading">
        <Loading />
      </div>

      <template v-else>
        <div v-if="displayedJobs.length > 0" class="jobs-grid">
          <router-link
            v-for="job in displayedJobs"
            :key="job.jobId"
            :to="{ name: 'JobDetails', params: { jobId: job.jobId } }"
            class="jobs-grid__link"
          >
            <JobCard
              :company="companyName(job)"
              :level="job.level"
              :title="job.title"
              :meta="[getLocation(job), job.workModel || 'Remoto']"
              :reward="job.rewardType?.title || 'A combinar'"
            >
              <template #company-icon>
                <span class="jobs-grid__avatar-letter">{{ companyInitial(job) }}</span>
              </template>
            </JobCard>
          </router-link>
        </div>

        <p v-else class="jobs-list__empty">
          {{ searchQuery
            ? `Nenhuma vaga encontrada para "${searchQuery}" nesta página.`
            : 'Não há vagas disponíveis no momento :(' }}
        </p>
      </template>

      <Pagination v-if="totalJobs > 0" :total="totalJobs" />
    </div>
  </div>
</template>

<script>
import {
  computed, onMounted, onBeforeMount, reactive, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import cache from '../../../api/jobs/cache';
import categoryServiceApi from '../../../api/categories';
import hiringTypeServiceApi from '../../../api/hiringType';
import periodServiceApi from '../../../api/period';
import workModelServiceApi from '../../../api/work-model';
import statesApi from '../../../api/states';
import Eyebrow from '../../ui/Eyebrow.vue';
import FilterChip from '../../ui/FilterChip.vue';
import Loading from '../Loading.vue';
import JobCard from '../../ui/JobCard.vue';
import Pagination from '../Pagination.vue';

export default {
  name: 'JobList',
  components: {
    Eyebrow,
    FilterChip,
    JobCard,
    Loading,
    Pagination,
  },
  setup() {
    const store = useStore();

    const state = reactive({
      loading: false,
      options: [],
      page: 1,
      openGroupKey: null,
    });

    const searchQuery = ref('');

    const addOptions = (title, key, id, data, defaultDescription = 'Todos', defaultValueOption = 0) => {
      state.options.push({
        title,
        key,
        id,
        data: [
          {
            description: defaultDescription,
            value: defaultValueOption,
            default: true,
          },
          ...data,
        ],
      })
    }

    const getAllCategories = async () => {
      const result = await categoryServiceApi.getAll();
      const { data } = result;
      const categories = data.categories.map((x) => {
        const { description, categoryId } = x;
        return {
          description,
          value: categoryId,
          default: false,
        };
      });

      addOptions('Categoria', 'categoryId', 'category', categories, 'Todas');
    }

    const getAllHiringType = async () => {
      const result = await hiringTypeServiceApi.getAll();
      const { data } = result;
      const categories = data.map((x) => {
        const { title, hiringTypeId } = x;
        return {
          description: title,
          value: hiringTypeId,
          default: false,
        };
      });

      addOptions('Contratação', 'hiringTypeId', 'hiring-type', categories);
    }

    const getAllPeriods = async () => {
      const result = await periodServiceApi.getAll();
      const { data } = result;
      const periods = data.map((x) => {
        const { title, periodId } = x;
        return {
          description: title,
          value: periodId,
          default: false,
        };
      });

      addOptions('Período', 'periodId', 'period', periods);
    }

    const getAllWorkModel = async () => {
      const result = await workModelServiceApi.getAll();
      const { data } = result;
      const periods = data.map((x) => {
        const { title, workModelId } = x;
        return {
          description: title,
          value: workModelId,
          default: false,
        };
      });

      addOptions('Modelo de Trabalho', 'workModelId', 'workModel', periods);
    }

    const getStates = async () => {
      const statesList = statesApi.map((x) => {
        const description = `${x.name}/${x.initials}`;
        return {
          description,
          value: description,
          default: false,
        };
      });

      addOptions('Localização', 'location', 'location', statesList, 'Todas', '');
    }

    const getJobs = async (page = 1, filter = {}) => {
      store.dispatch('getJobs', { jobsFilters: { page, ...filter } })
    };

    watch(store.getters.jobsFilters, async (filter) => {
      await getJobs(state.page, filter);
    });

    onMounted(async () => {
      await getJobs(state.page);
    });

    onBeforeMount(async () => {
      await getAllCategories();
      await getAllHiringType();
      await getAllPeriods();
      await getAllWorkModel();
      getStates();
    });

    const selectFilter = (group, item) => {
      store.dispatch('setFilterJobs', { [group.key]: item.value });

      const previousDefault = group.data.find((x) => x.default);
      if (previousDefault) previousDefault.default = false;
      item.default = true;
    };

    const resetFilters = () => {
      const resetPayload = {};
      state.options.forEach((group) => {
        const [firstOption] = group.data;
        resetPayload[group.key] = firstOption.value;
        group.data.forEach((item, index) => {
          item.default = index === 0;
        });
      });
      store.dispatch('setFilterJobs', resetPayload);
    };

    const toggleGroup = (key) => {
      state.openGroupKey = state.openGroupKey === key ? null : key;
    };

    const activeFilters = computed(() => state.options
      .map((group) => {
        const selected = group.data.find((item) => item.default);
        if (!selected || selected === group.data[0]) return null;

        return {
          groupKey: group.key,
          label: selected.description,
          group,
        };
      })
      .filter(Boolean));

    const removeFilter = (entry) => {
      const [firstOption] = entry.group.data;
      entry.group.data.forEach((item, index) => {
        item.default = index === 0;
      });
      store.dispatch('setFilterJobs', { [entry.groupKey]: firstOption.value });
    };

    const renderSalary = (salary) => {
      const [initial, final] = salary.split('-');

      const real = Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });

      return `${real.format(initial)} - ${real.format(final)}`
    }

    const getLocation = ({ location, country }) => {
      if (location && location !== '') {
        return location
      }

      return country
    }

    const companyName = (job) => (typeof job.company === 'string' ? job.company : job.company?.name) || 'Empresa';
    const companyInitial = (job) => companyName(job).charAt(0).toUpperCase();

    const jobs = computed(() => store.state.jobs.jobs.data);
    const totalJobs = computed(() => store.state.jobs.jobs.total);
    const hasFilter = computed(() => Object.keys(store.state.jobs.jobsFilters).length > 0);

    const usingCacheFallback = computed(() => (
      !(jobs.value && jobs.value.length > 0)
      && cache.jobs.length > 0
      && !hasFilter.value
    ));

    const baseJobs = computed(() => (usingCacheFallback.value ? cache.jobs : (jobs.value || [])));

    const displayedJobs = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return baseJobs.value;

      return baseJobs.value.filter((job) => (
        job.title?.toLowerCase().includes(query)
        || companyName(job).toLowerCase().includes(query)
      ));
    });

    const heroStatValue = computed(() => totalJobs.value || cache.jobs.length);

    const countLabel = computed(() => {
      const total = displayedJobs.value.length;
      return `${total} vaga${total === 1 ? '' : 's'} encontrada${total === 1 ? '' : 's'}`;
    });

    return {
      state,
      cache,
      searchQuery,
      jobs,
      displayedJobs,
      totalJobs,
      heroStatValue,
      countLabel,
      jobsLoading: computed(() => store.state.jobs.jobsLoading),
      hasFilter,
      activeFilters,
      toggleGroup,
      removeFilter,
      selectFilter,
      resetFilters,
      renderSalary,
      getLocation,
      companyName,
      companyInitial,
    };
  },
};
</script>

<style scoped>
.jobs-hero {
  padding: 72px 24px 64px;
  background: var(--navy-mid);
}

.jobs-hero__inner {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-md);
  text-align: center;
}

.jobs-hero__title {
  font-family: var(--font-brand);
  font-size: 44px;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--white);
}

.jobs-hero__sub {
  max-width: 540px;
  font-family: var(--font-brand);
  font-size: 16px;
  line-height: 1.6;
  color: var(--muted);
}

.jobs-hero__search {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-sm);
  width: 100%;
  max-width: 640px;
  margin-top: var(--gap-sm);
}

.jobs-hero__search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 52px;
  padding: 0 6px 0 20px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-pill);
}

.jobs-hero__search-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--muted);
}

.jobs-hero__search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--white);
}

.jobs-hero__search-input::placeholder {
  color: var(--faint);
}

.jobs-hero__search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 28px;
  border-radius: var(--r-pill);
  background: var(--cyan);
  color: var(--navy-deep);
  font-family: var(--font-brand);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.jobs-hero__search-note {
  font-family: var(--font-brand);
  font-size: 12px;
  color: var(--faint);
}

.jobs-hero__stats {
  display: flex;
  gap: var(--gap-xl);
  margin-top: var(--gap-sm);
}

.jobs-hero__stat {
  display: flex;
  align-items: center;
  gap: 8px;
}

.jobs-hero__stat-val {
  font-family: var(--font-brand);
  font-size: 16px;
  font-weight: 800;
  color: var(--cyan);
}

.jobs-hero__stat-label {
  font-family: var(--font-brand);
  font-size: 13px;
  color: var(--muted);
}

.jobs-main {
  display: flex;
  align-items: flex-start;
  gap: var(--gap-xl);
  max-width: 1280px;
  margin: 0 auto;
  padding: 48px 24px;
}

.jobs-sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-md);
  padding: 24px;
}

.jobs-sidebar__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.jobs-sidebar__title {
  font-family: var(--font-brand);
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
}

.jobs-sidebar__clear {
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-brand);
  font-size: 12px;
  font-weight: 600;
  color: var(--cyan);
}

.jobs-sidebar__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.jobs-sidebar__tag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  padding: 6px 10px;
  background: var(--cyan-glow);
  border: 1px solid var(--cyan);
  border-radius: var(--r-pill);
  cursor: pointer;
  font-family: var(--font-brand);
  font-size: 12px;
  font-weight: 600;
  color: var(--cyan);
}

.jobs-sidebar__tag svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.jobs-sidebar__group {
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--navy-border);
}

.jobs-sidebar__group:last-child {
  border-bottom: none;
}

.jobs-sidebar__group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 0 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.jobs-sidebar__group-title {
  font-family: var(--font-brand);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.jobs-sidebar__group-chevron {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--muted);
  transition: transform 0.2s var(--ease);
}

.jobs-sidebar__group-chevron--open {
  transform: rotate(180deg);
}

.jobs-sidebar__group-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s var(--ease);
}

.jobs-sidebar__group-body--open {
  grid-template-rows: 1fr;
}

.jobs-sidebar__chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
  min-height: 0;
}

.jobs-sidebar__chips :deep(.ui-chip) {
  width: 100%;
  justify-content: flex-start;
}

.jobs-sidebar__chips :deep(.ui-chip__label) {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.jobs-list {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.jobs-list__count {
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--muted);
}

.jobs-list__loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.jobs-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.jobs-grid__link {
  display: flex;
  min-width: 0;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

.jobs-grid__avatar-letter {
  font-family: var(--font-brand);
  font-weight: 800;
  font-size: 14px;
  color: var(--cyan);
}

.jobs-list__empty {
  padding: 48px 0;
  text-align: center;
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--muted);
}

@media (max-width: 1024px) {
  .jobs-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .jobs-main {
    flex-direction: column;
  }

  .jobs-sidebar {
    width: 100%;
  }

  .jobs-hero__title {
    font-size: 32px;
  }
}

@media (max-width: 600px) {
  .jobs-grid {
    grid-template-columns: 1fr;
  }
}
</style>
